import { useEffect, useMemo, useState } from "react";
import {
  BellIcon,
  BriefcaseIcon,
  Building2Icon,
  ChevronRightIcon,
  CoinsIcon,
  PawPrintIcon,
  StarIcon,
  UserIcon,
} from "lucide-react";
import { useNavigate } from "react-router";
import { useDebounce } from "use-debounce";

import { CandidacyStatusBadge } from "./CandidacyStatusBadge";
import { Column } from "./Column";
import { JobStatusBadge } from "./JobStatusBadge";
import { Row } from "./Row";
import { Text } from "./Text";
import { StafferPetAdoptionWizard } from "./staffer-pets/StafferPetAdoptionWizard";
import { StafferPetPlayyard } from "./staffer-pets/StafferPetPlayyard";
import { StafferPetSafetyBoundary } from "./staffer-pets/StafferPetSafetyBoundary";
import {
  StafferPetShopWizard,
  type ShopCategory,
} from "./staffer-pets/StafferPetShopWizard";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

import {
  STAFFER_PET_OPEN_PLAYYARD_EVENT,
  STAFFER_PET_OPEN_SHOP_EVENT,
  useStafferPet,
} from "@/hooks/useStafferPets";
import { useGetNotifications } from "@/hooks/queries/useGetNotifications";
import { useGlobalSearch } from "@/hooks/queries/useGlobalSearch";
import { useUpdateSetNotifications } from "@/hooks/updateSets/useUpdateSetNotifications";
import {
  notificationCategoryToMeta,
  notificationTypeToCategoryId,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { misc } from "@/lib/misc";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { getNotificationLink } from "@/lib/notificationLinks";
import { cn } from "@/lib/utils";
import { globalSearchStore } from "@/stores/globalSearch";
import {
  NotificationType,
  type NotificationCommsToSendPayload,
  type NotificationExpiringCandidatesPayload,
  type NotificationResponse,
  type NotificationStaleCandidaciesPayload,
} from "@/types";

const getHighestScore = (scores: (number | null)[]): number =>
  scores.reduce<number>(
    (highestScore, score) => Math.max(highestScore, score || 0),
    0,
  );

const getNotificationPriorityScore = (
  notification: NotificationResponse,
): number => {
  switch (notification.type) {
    // Top candidates outrank everything else, recommended or high-scoring.
    case NotificationType.EXCELLENT_CANDIDATE:
    case NotificationType.INTERNAL_RECOMMENDATION:
      return 5;
    case NotificationType.EXPIRING_CANDIDATES:
      return getHighestScore(
        (
          notification.payload as NotificationExpiringCandidatesPayload
        ).candidacies.map((candidacy) => candidacy.candidateRating),
      );
    case NotificationType.COMMS_TO_SEND:
      return getHighestScore(
        (
          notification.payload as NotificationCommsToSendPayload
        ).candidacies.map((candidacy) => candidacy.candidateRating),
      );
    case NotificationType.STALE_CANDIDACIES:
      return getHighestScore(
        (
          notification.payload as NotificationStaleCandidaciesPayload
        ).jobs.flatMap((job) =>
          job.candidacies.map((candidacy) => candidacy.candidateRating),
        ),
      );
    default:
      return 0;
  }
};

export const AppGlobalSearchCommandPalette = () => {
  const [search, setSearch] = useState("");
  const [debouncedQuery] = useDebounce(search, 250);
  const navigate = useNavigate();
  const isPetCommand = search.trim().toLowerCase() === "/pet";

  const { data } = useGlobalSearch(isPetCommand ? "" : debouncedQuery);
  const { data: notifications = [] } = useGetNotifications();
  const pet = useStafferPet();
  const { mutate: updateNotifications } = useUpdateSetNotifications();

  const isOpen = globalSearchStore((state) => state.isOpen);
  const setIsOpen = globalSearchStore((state) => state.setIsOpen);
  const [isPetPlayyardOpen, setIsPetPlayyardOpen] = useState(false);
  const [isPetWizardOpen, setIsPetWizardOpen] = useState(false);
  const [isPetShopOpen, setIsPetShopOpen] = useState(false);
  const [petShopCategory, setPetShopCategory] = useState<ShopCategory | null>(
    null,
  );
  const activeUnreadNotifications = useMemo(
    () =>
      notifications.filter(
        (notification) =>
          notification.readAt === null && notification.dismissedAt === null,
      ),
    [notifications],
  );
  const priorityNotification = useMemo(
    () =>
      [...activeUnreadNotifications].sort((left, right) => {
        const priorityDifference =
          getNotificationPriorityScore(right) -
          getNotificationPriorityScore(left);
        if (priorityDifference !== 0) return priorityDifference;
        return (
          new Date(right.createdAt).getTime() -
          new Date(left.createdAt).getTime()
        );
      })[0] || null,
    [activeUnreadNotifications],
  );
  const priorityNotificationScore =
    priorityNotification === null
      ? 0
      : getNotificationPriorityScore(priorityNotification);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        mixpanelUtils.trackKeyboardShortcutUsed(
          "Cmd+K",
          MIXPANEL_SCREENS.GLOBAL_SEARCH,
        );
        setIsOpen(!globalSearchStore.getState().isOpen);
      }
    };
    document.addEventListener("keydown", down);
    return () => {
      document.removeEventListener("keydown", down);
    };
  }, [setIsOpen]);

  // Surfaces outside this tree (the sidebar's incentive token) ask for the
  // shop on a specific tab through a window event.
  useEffect(() => {
    const handleOpenShop = (event: Event) => {
      const openShopEvent = event as CustomEvent<ShopCategory>;
      setPetShopCategory(openShopEvent.detail);
      setIsPetPlayyardOpen(false);
      setIsPetWizardOpen(false);
      setIsPetShopOpen(true);
    };

    window.addEventListener(STAFFER_PET_OPEN_SHOP_EVENT, handleOpenShop);
    return () => {
      window.removeEventListener(STAFFER_PET_OPEN_SHOP_EVENT, handleOpenShop);
    };
  }, []);

  // The sidebar pet opens the playyard the same way.
  useEffect(() => {
    const handleOpenPlayyard = () => {
      setIsPetWizardOpen(false);
      setIsPetShopOpen(false);
      setIsPetPlayyardOpen(true);
    };

    window.addEventListener(
      STAFFER_PET_OPEN_PLAYYARD_EVENT,
      handleOpenPlayyard,
    );
    return () => {
      window.removeEventListener(
        STAFFER_PET_OPEN_PLAYYARD_EVENT,
        handleOpenPlayyard,
      );
    };
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    setSearch("");
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");

    const formattedText = misc.formatPhoneNumberOnPaste(pastedText);
    setSearch(formattedText);
  };

  const handlePetCommand = () => {
    handleClose();
    setIsPetPlayyardOpen(true);
  };

  const handleNotificationsCommand = () => {
    handleClose();
    void navigate("/notifications");
  };

  const handlePriorityNotification = () => {
    if (priorityNotification === null) return;
    const notificationLink = getNotificationLink(
      priorityNotification.type,
      priorityNotification.payload,
    );
    updateNotifications({
      notificationIds: [priorityNotification.id],
      markAsRead: true,
    });
    handleClose();
    void navigate(notificationLink || "/notifications");
  };

  const handlePetAdopt = () => {
    setIsPetPlayyardOpen(false);
    setIsPetWizardOpen(true);
  };

  const handlePetShop = () => {
    setPetShopCategory(null);
    setIsPetPlayyardOpen(false);
    setIsPetShopOpen(true);
  };

  const handlePetWizardBack = () => {
    setIsPetWizardOpen(false);
    setIsPetShopOpen(false);
    setIsPetPlayyardOpen(true);
  };

  const renderPriorityNotification = () => {
    if (priorityNotification === null) return null;
    const categoryId = notificationTypeToCategoryId[priorityNotification.type];
    // Not in the notification catalog, so this type is not supposed to be
    // rendered on the frontend or in Staffer at all
    if (!categoryId) return null;
    const { Icon: CategoryIcon, className: categoryClassName } =
      notificationCategoryToMeta[categoryId];

    return (
      <CommandGroup heading="Highest priority">
        <CommandItem
          className="mx-1 items-start gap-3 px-4 py-3"
          onSelect={handlePriorityNotification}
          value={`priority-notification-${priorityNotification.id.toString()}`}
        >
          <Row
            className={cn(
              "mt-0.5 h-9 w-9 shrink-0 items-center justify-center rounded-full",
              categoryClassName,
            )}
          >
            <CategoryIcon className="!h-4 !w-4" />
          </Row>
          <Column className="min-w-0 flex-1 gap-1">
            <Row className="items-center justify-between gap-3">
              <Text className="truncate text-sm font-semibold text-slate-900">
                {priorityNotification.title}
              </Text>
              {priorityNotificationScore > 0 ? (
                <Row className="shrink-0 items-center gap-1 text-orange-600">
                  <StarIcon className="h-3.5 w-3.5 fill-current" />
                  <Text className="text-xs font-bold text-orange-700">
                    {priorityNotificationScore.toString()}
                  </Text>
                </Row>
              ) : null}
            </Row>
            <Text className="line-clamp-2 text-xs font-normal text-slate-600">
              {priorityNotification.content}
            </Text>
            <Text className="text-[11px] font-normal text-slate-400">
              {dates.timeAgoForComment(priorityNotification.createdAt)}
            </Text>
          </Column>
          <ChevronRightIcon className="mt-2 h-4 w-4 shrink-0 text-slate-400" />
        </CommandItem>
      </CommandGroup>
    );
  };

  const renderFrequentList = () => {
    const quickMenuItems = [
      {
        value: "notifications",
        title: "Notifications",
        icon: BellIcon,
        chip: `${activeUnreadNotifications.length.toString()} unread`,
        onSelect: handleNotificationsCommand,
      },
      {
        value: "pets",
        title: "Pets",
        icon: PawPrintIcon,
        chipIcon: CoinsIcon,
        chipIconClassName: "text-amber-400",
        chip: pet.coinBalance.toLocaleString("en-US"),
        onSelect: handlePetCommand,
      },
    ];

    return (
      <CommandList className="max-h-[70vh] overflow-y-auto py-2">
        {renderPriorityNotification()}
        <CommandGroup heading="Quick menu">
          {quickMenuItems.map((item) => (
            <CommandItem
              className="mx-1 gap-2.5 px-3 py-2.5"
              key={item.value}
              onSelect={item.onSelect}
              value={item.value}
            >
              <item.icon className="!h-4 !w-4 shrink-0 text-slate-500" />
              <Text className="min-w-0 flex-1 truncate text-sm text-slate-700">
                {item.title}
              </Text>
              <Row className="shrink-0 items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5">
                {item.chipIcon ? (
                  <item.chipIcon
                    className={cn("!h-3 !w-3", item.chipIconClassName)}
                  />
                ) : null}
                <Text className="text-[11px] font-normal text-slate-500">
                  {item.chip}
                </Text>
              </Row>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    );
  };

  return (
    <>
      <CommandDialog
        dialogContentClassName="top-[10%] !translate-y-0 overflow-y-scroll max-w-[40vw] max-h-[80vh]"
        onOpenChange={(open) => {
          if (!open) {
            setSearch("");
          }
          setIsOpen(open);
        }}
        open={isOpen}
        shouldFilter={false}
      >
        <CommandInput
          className="pr-8"
          onPaste={handlePaste}
          onValueChange={setSearch}
          placeholder="Search by name, phone number, job title, or company name"
          value={search}
        />
        {!search.trim() && renderFrequentList()}
        {search.trim() && (
          <CommandList className="max-h-[100vh] overflow-y-scroll">
            {isPetCommand && (
              <CommandGroup heading="Commands">
                <CommandItem
                  onSelect={handlePetCommand}
                  value="/pet"
                  style={{ cursor: "pointer" }}
                >
                  <PawPrintIcon />
                  Open pets
                </CommandItem>
              </CommandGroup>
            )}
            {data && (
              <>
                {data.jobs.length === 0 &&
                data.companies.length === 0 &&
                data.candidacies.length === 0 ? (
                  <CommandEmpty>No results found.</CommandEmpty>
                ) : (
                  <>
                    {data.candidacies.length > 0 && (
                      <CommandGroup heading="Candidacies">
                        {data.candidacies.map((candidacy) => (
                          <CommandItem
                            style={{ cursor: "pointer" }}
                            key={candidacy.id}
                            onSelect={() => {
                              mixpanelUtils.trackRowClick(
                                "Global search candidacy",
                                MIXPANEL_SCREENS.GLOBAL_SEARCH,
                              );
                              void navigate(
                                `/companies/${candidacy.company.id}/jobs/${candidacy.job.id}?c=${candidacy.id}`,
                              );
                              handleClose();
                            }}
                          >
                            <UserIcon />
                            <div>
                              <Row className="items-center gap-2">
                                <p>
                                  {candidacy.firstName} {candidacy.lastName}
                                </p>
                                <p>({candidacy.phone})</p>
                              </Row>
                              <p className="text-[13px] text-slate-500">
                                {candidacy.company.name} - {candidacy.job.title}{" "}
                                {candidacy.count > 1 &&
                                  `(${candidacy.count.toString()})`}
                              </p>
                            </div>
                            <CandidacyStatusBadge
                              byAgent={candidacy.lastStatusChangeByAgent}
                              className="ml-auto"
                              status={candidacy.status}
                            />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                    {data.jobs.length > 0 && (
                      <CommandGroup heading="Jobs">
                        {data.jobs.map((job) => (
                          <CommandItem
                            style={{ cursor: "pointer" }}
                            key={job.id}
                            onSelect={() => {
                              mixpanelUtils.trackRowClick(
                                "Global search job",
                                MIXPANEL_SCREENS.GLOBAL_SEARCH,
                              );
                              void navigate(
                                `/companies/${job.company.id}/jobs/${job.id}`,
                              );
                              handleClose();
                            }}
                          >
                            <BriefcaseIcon />
                            {job.title} - {job.company.name}
                            <JobStatusBadge
                              className="ml-auto"
                              pausedAt={job.pausedAt}
                              pauseReason={job.pauseReason}
                              pauseType={job.pauseType}
                              status={job.status}
                            />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                    {data.companies.length > 0 && (
                      <CommandGroup heading="Companies">
                        {data.companies.map((company) => (
                          <CommandItem
                            style={{ cursor: "pointer" }}
                            key={company.id}
                            onSelect={() => {
                              mixpanelUtils.trackRowClick(
                                "Global search company",
                                MIXPANEL_SCREENS.GLOBAL_SEARCH,
                              );
                              void navigate(`/companies/${company.id}`);
                              handleClose();
                            }}
                          >
                            <Building2Icon />
                            {company.name} - {company.location}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </>
                )}
              </>
            )}
          </CommandList>
        )}
      </CommandDialog>
      <StafferPetSafetyBoundary>
        <StafferPetPlayyard
          open={isPetPlayyardOpen}
          onAdopt={handlePetAdopt}
          onOpenChange={setIsPetPlayyardOpen}
          onShop={handlePetShop}
        />
        <StafferPetAdoptionWizard
          open={isPetWizardOpen}
          onBack={handlePetWizardBack}
          onComplete={() => {
            setIsPetWizardOpen(false);
            setIsPetPlayyardOpen(true);
          }}
          onOpenChange={setIsPetWizardOpen}
        />
        <StafferPetShopWizard
          initialCategory={petShopCategory}
          open={isPetShopOpen}
          onBack={handlePetWizardBack}
          onOpenChange={setIsPetShopOpen}
        />
      </StafferPetSafetyBoundary>
    </>
  );
};
